import React, { useCallback, useEffect, useState } from 'react';
import { useModalStore, useStore } from 'src/hooks/useStore';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import QueryString from 'qs';
import styled from 'styled-components/macro';
import { FaExclamationCircle } from 'react-icons/fa';
import Tooltip from 'src/components/ui-kit/Tooltip';
import Modal from 'src/components/ui-kit/Modal';
import * as colors from 'src/styles/colors';
import { mobilePoint } from 'src/styles/globalStyle';
import SearchPage, {
  FormItem,
  Title,
  Content,
  Symbol,
  Button,
  FooterLink,
  ErrorMsg,
} from './SearchPage';
import ReactSelect from 'src/components/ui-kit/ReactSelect';
import Input from 'src/components/ui-kit/Input';
import DatePicker from 'src/components/ui-kit/DatePicker';

import { formateDate, stringToDate } from 'src/utils/formatters';
import { globalComponents } from 'src/types/globalTypes';
import { CustomRadioButton } from 'src/styles/radio';
import {
  ApplyClass,
  ApplNow,
  PatentSearchNoOptions,
  PatentSearchNameOptions,
  IReqPatentRightsPage,
  PatentOrderOptions,
} from 'src/types/api/patentRightsApiTypes';
import { FaArrowRight } from 'react-icons/fa';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from 'src/components/layout/pagination/Pagination';
import AlertModal from 'src/components/ui-kit/AlertModal';

const ToolText = styled.div`
  font-size: 18px;
  display: flex;
  align-items: center;
`;

const Icon = styled.div`
  color: #975EBB;
`;

const ModalWrapper = styled.div`
  width: 100%;
  padding: 32px 40px;
  text-align: center;
`;

const ModalContent = styled.div`
  display: flex;
  font-size: 16px;
  line-height: 40px;
  color: ${colors.BlueGray_800};
  gap: 5px;
  text-align: left;

  @media (max-width: ${mobilePoint}) {
    display: grid;
  }
`;

const ModalButtonDiv = styled.div`
  display: grid;
  gap: 1em;
  grid-template-columns: 1fr 1fr;
  justify-items: center;
`;

const ModalButton = styled.button<{ isCancel?: boolean }>`
  width: max-content;
  height: 40px;
  border-radius: 4px;
  background-color: ${(prop) => (prop.isCancel ? colors.notice_orange : colors.primaryGreen_400)};
  color: ${colors.white};
  font-size: 18px;
  margin-top: 2em;
  padding: 0 1em;
  cursor: pointer;
`;

/**
 * 專利搜尋頁面
 */
const PatentRights: React.FC = () => {
  const closeModal = useModalStore((state) => state.closeModal);
  const isMobile = useStore((state) => state.isMobile);
  const navigate = useNavigate();
  const { control, register, reset, getValues } = useForm<IReqPatentRightsPage>();
  //日期間隔符號
  const dateInterval = '';
  // 欄位檢測
  const [isCheckedOk, setIsCheckedOk] = useState<boolean | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const setActiveModal = useModalStore((state) => state.setActiveModal);

  // 頁面元素資訊
  const component = globalComponents.P;

  // 因重複過多次跑sonar issue，進行重構
  const datePickerFun = (onChange: (e: any) => void, value?: string, datePickerId?: string) => (
    <DatePicker
      width="45%"
      onChange={(date) => onChange(formateDate(date, dateInterval))}
      selected={stringToDate(value)}
      primaryColors={component.color}
	  datePickerId={datePickerId}
    />
  );

  const inputFun = (onChange: (e: any) => void, inputId?: string) => (
    <Input
      primaryColors={component.color}
      onChange={onChange}
      width="100%"
      isBigForm={isMobile ? true : false}
	  inputId={inputId}
    />
  );

  const inputPluralFun = (onChange: (e: any) => void, airaLabel?: string) => (
    <Input
      placeholder={"多筆以','隔開"}
      primaryColors={component.color}
      onChange={onChange}
      width="100%"
      isBigForm={isMobile ? true : false}
	  aria-label={airaLabel || "輸入欄位"}
    />
  );

  /** 取得當前欄位應有字數 */
  const getNum = (type: string) => {
    switch (type) {
      case 'patentno':
      case 'publishno':
        return 7;
      case 'noticeno':
        return 9;
      case 'applno':
        return 12;
    }
  };

  /** 專利查詢欄位檢測 */
  const checkNumPatent = (type: string, content: string | null) => {
    let flag = true;
    if (content) {
      const words = content.split(',');
      const num = getNum(type);
      for (const item of words) {
        const regex = new RegExp('^[a-zA-Z\\d]{0,' + num + '}$');
        flag = regex.test(item);
        if (!flag) {
          break;
        }
      }
    }
    return flag;
  };

  /** 欄位是否至少輸入一項 */
  const checkIfSearchLeastOne = (params: IReqPatentRightsPage) => {
    const checkKeywordValue = (param: [string, any]) => {
      let flag = false;
      if (param[1]) {
        // 因部分欄位值的型態為物件，若非物件則可直接判斷有值；為物件，則檢查物件項目的值是否存在
        flag = typeof param[1] === 'object' ? !!param[1].value : true;
      }
      return flag;
    };
    const searchParams = Object.entries(params).filter((item) => checkKeywordValue(item)).length;
    return searchParams >= 6;
  };

  /** 日期欄位是否為合法範圍 */
  const checkIfDateRangeLegal = (publishBDate: string | undefined, publishEDate: string | undefined, chargeExpirBDate: string | undefined, chargeExpirEDate: string | undefined) => {
    let publishDate = true;
    let chargeExpirDate = true;

    if (publishBDate && publishEDate) {
      const date1 = new Date(`${publishBDate.slice(0, 4)}-${publishBDate.slice(4, 6)}-${publishBDate.slice(6)}`);
      const date2 = new Date(`${publishEDate.slice(0, 4)}-${publishEDate.slice(4, 6)}-${publishEDate.slice(6)}`);
      publishDate = date2.getFullYear() - date1.getFullYear() < 5;
    }

    if (chargeExpirBDate && chargeExpirEDate) {
      const date1 = new Date(`${chargeExpirBDate.slice(0, 4)}-${chargeExpirBDate.slice(4, 6)}-${chargeExpirBDate.slice(6)}`);
      const date2 = new Date(`${chargeExpirEDate.slice(0, 4)}-${chargeExpirEDate.slice(4, 6)}-${chargeExpirEDate.slice(6)}`);
      chargeExpirDate = date2.getFullYear() - date1.getFullYear() < 5;
    }

    return publishDate && chargeExpirDate;
  };

  /** 驗證日期是否僅選起日或僅選迄日 */
  const checkIfDateSetBothSide = (publishBDate: string | undefined, publishEDate: string | undefined, chargeExpirBDate: string | undefined, chargeExpirEDate: string | undefined) => {
    let isOnlySetOneSide = true;

    if ((publishBDate === undefined && publishEDate !== undefined) || (publishBDate !== undefined && publishEDate === undefined) || (chargeExpirBDate === undefined && chargeExpirEDate !== undefined) || (chargeExpirBDate !== undefined && chargeExpirEDate === undefined)) {
      isOnlySetOneSide = false;
    }

    return isOnlySetOneSide;
  };

  const setDateRange = (publishBDate: string | undefined, publishEDate: string | undefined, chargeExpirBDate: string | undefined, chargeExpirEDate: string | undefined) => {
    var modalPublishBDate = ''
    var modalPublishEDate = ''
    var modalChargeExpirBDate = ''
    var modalChargeExpirEDate = ''

    if ((publishBDate === undefined && publishEDate !== undefined)) {
      modalPublishBDate = (parseInt(publishEDate.substring(0, 4)) - 5) + publishEDate.substring(4, 8)
      modalPublishEDate = publishEDate
    }

    if ((publishBDate !== undefined && publishEDate === undefined)) {
      modalPublishBDate = publishBDate
      modalPublishEDate = (parseInt(publishBDate.substring(0, 4)) + 5) + publishBDate.substring(4, 8)
    }

    if ((chargeExpirBDate === undefined && chargeExpirEDate !== undefined)) {
      modalChargeExpirBDate = (parseInt(chargeExpirEDate.substring(0, 4)) - 5) + chargeExpirEDate.substring(4, 8)
      modalChargeExpirEDate = chargeExpirEDate
    }

    if ((chargeExpirBDate !== undefined && chargeExpirEDate === undefined)) {
      modalChargeExpirBDate = chargeExpirBDate
      modalChargeExpirEDate = (parseInt(chargeExpirBDate.substring(0, 4)) + 5) + chargeExpirBDate.substring(4, 8)
    }

    return <Modal isAlert={true} width={'28em'}>
      <ModalWrapper>
        <ModalContent>因搜尋日期區間限制5年，將以以下日期範圍查詢。</ModalContent>
        <ModalContent>
          {modalPublishBDate && modalPublishEDate ? `公告日期：${modalPublishBDate} ~ ${modalPublishEDate}` : ''}
        </ModalContent>
        <ModalContent>
          {modalChargeExpirBDate && modalChargeExpirEDate ? `年費有效日期：${modalChargeExpirBDate} ~ ${modalChargeExpirEDate}` : ''}
        </ModalContent>
        <ModalButtonDiv>
          <ModalButton
            onClick={() => {
              setIsCheckedOk(true);
              closeModal();
            }}
          >
            確定
          </ModalButton>
          <ModalButton isCancel onClick={() => closeModal()}>
            關閉
          </ModalButton>
        </ModalButtonDiv>
      </ModalWrapper>
    </Modal>
  }

  /** 處理插入錯誤訊息 */
  const handleErrMsg = useCallback(() => {
    setErrorMsg(
      PatentSearchNoOptions.find((item) => item.value === getValues('searchNo.keyWord'))?.label +
      '輸入格式有誤（' +
      getNum(getValues('searchNo.keyWord')?.toString() ?? "") +
      '碼英數字）',
    );
  }, [getValues]);

  //插入預設值
  useEffect(() => {
    reset({
      pageNo: FIRST_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: PatentOrderOptions[0].value,
      searchNo: { keyWord: PatentSearchNoOptions[0].value },
      searchName: { keyWord: PatentSearchNameOptions[0].value },
    });
  }, [reset]);

  // 進行查詢
  useEffect(() => {
    isCheckedOk &&
      navigate({
        pathname: process.env.PUBLIC_URL + '/patentRights/result',
        search: QueryString.stringify(getValues()),
      });
  }, [isCheckedOk, navigate, getValues]);

  // 插入錯誤訊息
  useEffect(() => {
    isCheckedOk === false && handleErrMsg();
  }, [isCheckedOk, handleErrMsg]);

  const annotation = (
    <div>
      本系統資訊僅供參考，所有資料以經濟部智慧財產局公告為準。
      <br />
      連結參考政府資料開放平臺下載網址。專利權狀態異動資料：
      <FooterLink href="https://data.gov.tw/dataset/30126" target="_blank">
        發明
      </FooterLink>
      ｜
      <FooterLink href="https://data.gov.tw/dataset/30125" target="_blank">
        新型
      </FooterLink>
      ｜
      <FooterLink href="https://data.gov.tw/dataset/30127" target="_blank">
        設計
      </FooterLink>
    </div>
  );

  /** 申請類別轉換代碼。 */
  const changeApplClass = (applyClass: ApplyClass) => {
    switch (applyClass) {
      case ApplyClass.I:
        return '1';
      case ApplyClass.M:
        return '2';
      case ApplyClass.D:
        return '3';
      default:
        return;
    }
  };

  const form = (
    <>
      <FormItem>
        <Title required>專利類別(必填)</Title>
        <Content>
          {Object.keys(ApplyClass).map((key, index) => (
            <CustomRadioButton primaryColors={component.color} key={key}>
              <label>
                <input
                  type="radio"
                  value={changeApplClass(ApplyClass[key as keyof typeof ApplyClass])}
                  defaultChecked={index === 0}
				  title={`專利類別選項-${ApplyClass[key as keyof typeof ApplyClass]}`}
                  {...register('applyClass')}
                />
                <span />
                {`${ApplyClass[key as keyof typeof ApplyClass]}`}
              </label>
            </CustomRadioButton>
          ))}
        </Content>
      </FormItem>
      <FormItem>
        <Title required>專利是否存續(必填)</Title>
        <Content>
          {Object.keys(ApplNow).map((key, index) => (
            <CustomRadioButton primaryColors={component.color} key={key}>
              <label>
                <input
                  type="radio"
                  value={key}
                  defaultChecked={index === 0}
				  title={`專利是否存續選項-${ApplNow[key as keyof typeof ApplNow]}`}
                  {...register('isEffective')}
                />
                <span />
                {`${ApplNow[key as keyof typeof ApplNow]}`}
              </label>
            </CustomRadioButton>
          ))}
        </Content>
      </FormItem>
      <FormItem>
        <Title>
          <Controller
            control={control}
            name={`searchNo.keyWord`}
            render={({ field: { onChange, value } }) => (
              <ReactSelect
                options={PatentSearchNoOptions}
                primaryColors={component.color}
                onChange={onChange}
                value={value}
                isBigForm={isMobile ? true : false}
				inputId="input-searchNo-keyWord"
				ariaLabelValue="搜尋號下拉式選單"
              />
            )}
          />
        </Title>
        <Content isWrong={!!getValues('searchNo.value') && !!errorMsg}>
          <Controller
            control={control}
            name={`searchNo.value`}
            render={({ field: { onChange } }) => inputPluralFun(onChange, '輸入號碼')}
          />
          <ErrorMsg>{getValues('searchNo.value') && errorMsg}</ErrorMsg>
        </Content>
      </FormItem>
      {isMobile ? (
        <FormItem>
          <Content>
            <Controller
              control={control}
              name={`publishBDate`}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  primaryColors={component.color}
                  placeholderTitle={'公告日期'}
                  onChange={(date) => onChange(formateDate(date, dateInterval))}
                  selected={stringToDate(value)}
                />
              )}
            />

            <Controller
              control={control}
              name={`publishEDate`}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  primaryColors={component.color}
                  placeholderTitle={'至'}
                  onChange={(date) => onChange(formateDate(date, dateInterval))}
                  selected={stringToDate(value)}
                />
              )}
            />
          </Content>
        </FormItem>
      ) : (
        <FormItem>

          <Title>
		    <label htmlFor="datePicker-publishBDate">公告日期</label>
            <div style={{ float: 'right' }} title="公告日期搜尋區間為5年">
              <Tooltip overlay={"公告日期搜尋區間為5年"} bgColor={component.color.sixth}>
                <ToolText>
                  <Icon>
                    <FaExclamationCircle />
                  </Icon>
                </ToolText>
              </Tooltip>
            </div>
          </Title>
          <Content>
            <Controller
              control={control}
              name={`publishBDate`}
              render={({ field: { onChange, value } }) => datePickerFun(onChange, value, 'datePicker-publishBDate')}
            />
            <Symbol><label htmlFor="datePicker-publishEDate">~</label></Symbol>
            <Controller
              control={control}
              name={`publishEDate`}
              render={({ field: { onChange, value } }) => datePickerFun(onChange, value, 'datePicker-publishEDate')}
            />
          </Content>
        </FormItem>

      )}
      {isMobile ? (
        <FormItem>
          <Content>
            <Controller
              control={control}
              name={`patentName`}
              render={({ field: { onChange } }) => (
                <Input
                  primaryColors={component.color}
                  customizedTitle="專利中英文名稱"
                  onChange={onChange}
                  isBigForm
                />
              )}
            />
          </Content>
        </FormItem>
      ) : (
        <FormItem>
          <Title><label htmlFor="input-patentName">專利中英文名稱</label></Title>
          <Content>
            <Controller
              control={control}
              name={`patentName`}
              render={({ field: { onChange } }) => inputFun(onChange, 'input-patentName')}
            />
          </Content>
        </FormItem>
      )}
      <FormItem>
        <Title>
          <Controller
            control={control}
            name={`searchName.keyWord`}
            render={({ field: { onChange, value } }) => (
              <ReactSelect
                options={PatentSearchNameOptions}
                primaryColors={component.color}
                onChange={onChange}
                value={value}
                isBigForm={isMobile ? true : false}
				inputId="input-searchName-keyWord"
				ariaLabelValue="搜尋名稱下拉式選單"
              />
            )}
          />
        </Title>
        <Content>
          <Controller
            control={control}
            name={`searchName.value`}
            render={({ field: { onChange } }) => inputPluralFun(onChange, '輸入號碼')}
          />
        </Content>
      </FormItem>
      {isMobile ? (
        <FormItem>
          <Content>
            <Controller
              control={control}
              name={`chargeExpirBDate`}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  primaryColors={component.color}
                  placeholderTitle="年費有效日期"
                  onChange={(date) => onChange(formateDate(date, dateInterval))}
                  selected={stringToDate(value)}
                />
              )}
            />
            <Controller
              control={control}
              name={`chargeExpirEDate`}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  primaryColors={component.color}
                  placeholderTitle="至"
                  onChange={(date) => onChange(formateDate(date, dateInterval))}
                  selected={stringToDate(value)}
                />
              )}
            />
          </Content>
        </FormItem>
      ) : (
        <FormItem>
          <Title>
            <label htmlFor="datePicker-chargeExpirBDate">年費有效日期</label>
            <div style={{ float: 'right' }} title="年費有效日期搜尋區間為5年">
              <Tooltip overlay={"年費有效日期搜尋區間為5年"} offset={-2} bgColor={component.color.sixth}>
                <ToolText>
                  <Icon>
                    <FaExclamationCircle />
                  </Icon>
                </ToolText>
              </Tooltip>
            </div>
          </Title>
          <Content>
            <Controller
              control={control}
              name={`chargeExpirBDate`}
              render={({ field: { onChange, value } }) => datePickerFun(onChange, value, 'datePicker-chargeExpirBDate')}
            />
            <Symbol><label htmlFor="datePicker-chargeExpirEDate">~</label></Symbol>
            <Controller
              control={control}
              name={`chargeExpirEDate`}
              render={({ field: { onChange, value } }) => datePickerFun(onChange, value, 'datePicker-chargeExpirEDate')}
            />
          </Content>
        </FormItem>
      )}
    </>
  );

  return (
    <SearchPage
      title={component.title}
      annotation={annotation}
      bgImg={isMobile ? component.bgImgBannerMobile : component.bgImgBanner}
      image={component.hoverImage}
      bgColor={component.color.third}
      searchPageImgs={component.searchPageImgs}
    >
      {form}
      <Button
        onClick={() => {
          // 欄位檢查
          setIsCheckedOk(
            checkIfDateSetBothSide(getValues('publishBDate'), getValues('publishEDate'), getValues('chargeExpirBDate'), getValues('chargeExpirEDate')) &&
            checkIfDateRangeLegal(getValues('publishBDate'), getValues('publishEDate'), getValues('chargeExpirBDate'), getValues('chargeExpirEDate')) &&
            checkNumPatent(getValues('searchNo.keyWord')?.toString() ?? "", getValues('searchNo.value')?.toString() ?? "") &&
            checkIfSearchLeastOne(getValues()),
          );

          isCheckedOk === false && handleErrMsg();
          // 跳出提醒視窗
          !checkIfSearchLeastOne(getValues()) &&
            setActiveModal(
              <AlertModal>
                <div>請至少再輸入一項查詢條件。</div>
              </AlertModal>,
            );
          // 驗證日期是否僅選起日或僅選迄日
          !checkIfDateSetBothSide(getValues('publishBDate'), getValues('publishEDate'), getValues('chargeExpirBDate'), getValues('chargeExpirEDate')) &&
            setActiveModal(setDateRange(getValues('publishBDate'), getValues('publishEDate'), getValues('chargeExpirBDate'), getValues('chargeExpirEDate')));
          // 驗證日期區間是否和規
          !checkIfDateRangeLegal(getValues('publishBDate'), getValues('publishEDate'), getValues('chargeExpirBDate'), getValues('chargeExpirEDate')) &&
            setActiveModal(
              <AlertModal width="21em">
                <div>搜尋日期區間超過5年請重新選擇。</div>
              </AlertModal>,
            );
        }}
      >
        搜尋
        <span>
          <FaArrowRight />
        </span>
      </Button>
    </SearchPage>
  );
};

export default PatentRights;
